/**
 * LangContext.tsx
 * ─────────────────────────────────────────────────────────────
 * Single source of truth for:
 *   • Language  — "en" | "es"  (persisted in localStorage)
 *   • Dark mode — boolean       (persisted in localStorage via Tailwind "class" strategy)
 *
 * Usage:
 *   const { lang, setLang, dark, toggleDark } = useLang()
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react"

export type Lang = "en" | "es"

type LangState = {
  lang: Lang
  setLang: (l: Lang) => void
  dark: boolean
  toggleDark: () => void
}

const LangContext = createContext<LangState | undefined>(undefined)

function applyDark(dark: boolean) {
  document.documentElement.classList.toggle("dark", dark)
  document.documentElement.setAttribute("data-theme", dark ? "dark" : "light")
}

function applyLang(lang: Lang) {
  document.documentElement.setAttribute("lang", lang)
  document.documentElement.setAttribute("data-lang", lang)
}

export function LangProvider({ children }: { children: ReactNode }) {
  // ── Init from localStorage (or OS preference for dark) ──────────────────
  const [lang, setLangState] = useState<Lang>(() => {
    const stored = localStorage.getItem("lang")
    return stored === "es" ? "es" : "en"
  })

  const [dark, setDarkState] = useState<boolean>(() => {
    const stored = localStorage.getItem("theme")
    if (stored === "dark") return true
    if (stored === "light") return false
    return window.matchMedia("(prefers-color-scheme: dark)").matches
  })

  // ── Apply on mount ───────────────────────────────────────────────────────
  useEffect(() => {
    applyDark(dark)
    applyLang(lang)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Setters ──────────────────────────────────────────────────────────────
  const setLang = useCallback((l: Lang) => {
    setLangState(l)
    localStorage.setItem("lang", l)
    applyLang(l)
  }, [])

  const toggleDark = useCallback(() => {
    setDarkState((prev) => {
      const next = !prev
      localStorage.setItem("theme", next ? "dark" : "light")
      applyDark(next)
      return next
    })
  }, [])

  const value = useMemo<LangState>(
    () => ({ lang, setLang, dark, toggleDark }),
    [lang, setLang, dark, toggleDark]
  )

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>
}

export function useLang(): LangState {
  const ctx = useContext(LangContext)
  if (!ctx) throw new Error("useLang must be used within LangProvider")
  return ctx
}
